/**
 * SpaceDetailPage — 스페이스 상세
 * ChatRenderer 기반 채팅 UI (cbot-* CSS 공용)
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, useToast } from '../components/ui';
import { Modal } from '../components/ui';
import spacesApi, { Space, SpaceMessage, SpaceFile, SpaceSession } from '../api/spaces';
import { authApi, workflowApi, Workflow } from '../api';
import type { ChatSource } from '../api';
import type { User } from '../api/auth';
import apiClient from '../api/client';
import { ChatRenderer, Message } from '../components/chat/ChatRenderer';
import { ThemeEditorModal } from '../components/chat/ThemeEditorModal';
import { chatSessionApi, ChatTheme } from '../api/chat-sessions';
import { calcCostFront } from '../utils/model-pricing';
import './SpaceDetailPage.css';

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function SpaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const currentUser = authApi.getUser();

  // ── 스페이스 상태 ──
  const [space, setSpace] = useState<Space | null>(null);
  const [messages, setMessages] = useState<SpaceMessage[]>([]);
  const [overrideModel, setOverrideModel] = useState<string>('');
  const [sessions, setSessions] = useState<SpaceSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [loadingSpace, setLoadingSpace] = useState(true);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);

  // ── 테마 ──
  const [personalTheme, setPersonalTheme] = useState<ChatTheme | null>(null);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [savingTheme, setSavingTheme] = useState(false);

  // ── 스페이스 파일 ──
  const [realFiles, setRealFiles] = useState<SpaceFile[]>([]);

  const loadRealFiles = useCallback(async () => {
    if (!id) return;
    try {
      const files = await spacesApi.getFiles(id);
      setRealFiles(files);
    } catch { /* 무시 */ }
  }, [id]);

  // ── 스트리밍 상태 ──
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingTrace, setStreamingTrace] = useState<Array<Record<string, unknown>>>([]);
  const [streamingSources, setStreamingSources] = useState<ChatSource[]>([]);
  const [streamingStatus, setStreamingStatus] = useState('');
  const [typing, setTyping] = useState(false);

  const lastMsgKeyRef = useRef<string>('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── 스페이스 로드 ──
  useEffect(() => {
    if (!id) return;
    spacesApi.get(id).then(s => {
      setSpace(s);
      if (s.workflowId) workflowApi.get(s.workflowId).then(setWorkflow).catch(() => {});
      // 스페이스 테마 로드 (spaceId 기준으로 독립 저장)
      chatSessionApi.getTheme(id).then(theme => {
        if (theme) setPersonalTheme(theme);
      }).catch(() => {});
      return spacesApi.getSessions(id);
    }).then(sess => {
      setSessions(sess);
    }).catch(() => {
      showToast('error', '스페이스를 불러오는데 실패했습니다.');
    }).finally(() => setLoadingSpace(false));

    loadRealFiles();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 자동 스크롤 ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // ── 폴링 (10초) ──
  const pollMessages = useCallback(async () => {
    if (!id || !currentSessionId) return;
    try {
      const msgs = await spacesApi.getMessages(id, currentSessionId);
      setMessages(msgs);
    } catch { /* 무시 */ }
  }, [id, currentSessionId]);

  useEffect(() => {
    pollingRef.current = setInterval(pollMessages, 10000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [pollMessages]);

  // ── 메시지 전송 ──
  const handleSend = useCallback(async (text: string) => {
    if (!text.trim() || !id || typing) return;

    // 낙관적 업데이트
    const userMsg: SpaceMessage = {
      spaceId: id,
      sessionId: currentSessionId || 'local',
      chatId: `local-${Date.now()}`,
      senderEmail: currentUser?.email || '',
      senderName: currentUser?.name || currentUser?.email || '',
      userContent: text,
      startDateTime: new Date().toISOString(),
      status: 'pending',
    };
    setMessages(prev => [...prev, userMsg]);
    setTyping(true);
    setStreamingContent('');
    setStreamingTrace([]);
    setStreamingSources([]);
    setStreamingStatus('');

    try {
      const history = messages.slice(-20).flatMap(m => {
        const items: Array<{ role: 'user' | 'assistant'; content: string }> = [];
        if (m.userContent) items.push({ role: 'user', content: m.userContent });
        if (m.assistantContent) items.push({ role: 'assistant', content: m.assistantContent });
        return items;
      });

      const SSE_BASE = import.meta.env.DEV ? 'http://localhost:3002' : '';
      const token = apiClient.defaults.headers.common['Authorization'] as string
        || `Bearer ${localStorage.getItem('agentbuilder_token')}`;

      const response = await fetch(`${SSE_BASE}/api/spaces/${id}/messages/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
        body: JSON.stringify({ message: text, sessionId: currentSessionId || undefined, history, ...(overrideModel ? { modelId: overrideModel } : {}) }),
      });

      if (!response.ok) throw new Error('스트리밍 실패');
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let sources: ChatSource[] = [];
      let traceEvents: Array<Record<string, unknown>> = [];
      let receivedSessionId: string | null = null;

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;
        const raw = decoder.decode(value, { stream: true });
        for (const line of raw.split('\n')) {
          const l = line.trim();
          if (!l.startsWith('data:')) continue;
          try {
            const data = JSON.parse(l.slice(5).trim());
            if (data.type === 'text') {
              fullContent += data.data;
              setStreamingContent(fullContent);
              await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
            } else if (data.type === 'trace') {
              traceEvents = [...traceEvents, data.data];
              setStreamingTrace(traceEvents);
              await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
            } else if (data.type === 'sources') {
              sources = (data.data as ChatSource[]) ?? [];
              setStreamingSources(sources);
            } else if (data.type === 'bot_saved') {
              const sid = data.data?.sessionId as string | undefined;
              if (sid) {
                receivedSessionId = sid;
                if (data.data?.isNewSession) setCurrentSessionId(sid);
              }
            }
          } catch { /* 무시 */ }
        }
      }

      const finalSessionId = receivedSessionId || currentSessionId || '';
      const now2 = new Date().toISOString();
      setMessages(prev => [
        ...prev.filter(m => !m.chatId.startsWith('local-')),
        {
          sessionId: finalSessionId,
          chatId: '0',
          spaceId: id,
          senderEmail: currentUser?.email || '',
          senderName: currentUser?.name || currentUser?.email || '',
          userContent: text,
          assistantContent: fullContent,
          startDateTime: new Date().toISOString(),
          endDateTime: now2,
          status: 'complete' as const,
          sources,
          traceContent: traceEvents,
        },
      ]);

      if (receivedSessionId) {
        setCurrentSessionId(receivedSessionId);
        spacesApi.getSessions(id).then(setSessions).catch(() => {});
      }

      if (finalSessionId) {
        setTimeout(async () => {
          const msgs = await spacesApi.getMessages(id, finalSessionId).catch(() => null);
          // 서버 메시지가 더 많거나 같을 때만 교체 (로컬 상태가 최신이면 유지)
          if (msgs && msgs.length > 0) {
            setMessages(prev => msgs.length >= prev.length ? msgs : prev);
          }
        }, 1500);
      }

    } catch {
      showToast('error', '메시지 전송에 실패했습니다.');
    } finally {
      setTyping(false);
      setStreamingContent('');
      setStreamingTrace([]);
      setStreamingSources([]);
      setStreamingStatus('');
    }
  }, [id, messages, typing, currentUser, currentSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 멤버 초대 ──
  const [showInvite, setShowInvite] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteDropOpen, setInviteDropOpen] = useState(false);
  const [selInviteEmails, setSelInviteEmails] = useState<string[]>([]);
  const [inviting, setInviting] = useState(false);
  const [systemUsers, setSystemUsers] = useState<User[]>([]);

  useEffect(() => {
    authApi.listPeers().then(setSystemUsers).catch(() => {});
  }, []);

  const handleInvite = async () => {
    if (!id || selInviteEmails.length === 0) return;
    setInviting(true);
    try {
      for (const email of selInviteEmails) {
        await spacesApi.addMember(id, email);
      }
      const updated = await spacesApi.get(id);
      setSpace(updated);
      setSelInviteEmails([]);
      setShowInvite(false);
      setInviteSearch('');
      showToast('success', `${selInviteEmails.length}명 멤버를 추가했습니다.`);
    } catch (e: any) {
      const msg = e?.response?.data?.error || '멤버 추가에 실패했습니다.';
      showToast('error', msg);
    } finally {
      setInviting(false);
    }
  };

  // ── 파일 업로드 ──
  type UploadStatus = 'uploading' | 'indexing' | 'done' | 'error';
  const [uploadedFiles, setUploadedFiles] = useState<Array<{
    id: string; name: string; icon: string; bg: string; status: UploadStatus; step: string;
  }>>([]);

  function getFileStyle(name: string) {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf')  return { icon: '📄', bg: '#fee2e2' };
    if (['docx', 'doc'].includes(ext)) return { icon: '📝', bg: '#ede9fe' };
    if (['xlsx', 'xls'].includes(ext)) return { icon: '📊', bg: '#d1fae5' };
    if (['png', 'jpg', 'jpeg'].includes(ext)) return { icon: '🖼️', bg: '#dbeafe' };
    return { icon: '📋', bg: '#fef3c7' };
  }

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !id) return;
    for (const file of files) {
      const { icon, bg } = getFileStyle(file.name);
      const tempId = Math.random().toString(36).slice(2);
      setUploadedFiles(prev => [...prev, { id: tempId, name: file.name, icon, bg, status: 'uploading', step: '⬆ 업로드 중...' }]);
      try {
        await spacesApi.uploadFile(id, file);
        setUploadedFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: 'indexing', step: '🧠 인덱싱 중...' } : f));
        setTimeout(() => {
          setUploadedFiles(prev => prev.filter(f => f.id !== tempId));
          loadRealFiles();
        }, 2500);
      } catch {
        setUploadedFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: 'error', step: '✗ 업로드 실패' } : f));
        showToast('error', `${file.name} 업로드에 실패했습니다.`);
      }
    }
    e.target.value = '';
  };

  const [deleteFileConfirm, setDeleteFileConfirm] = useState<string | null>(null);

  const handleDeleteFile = (fileId: string) => {
    setDeleteFileConfirm(fileId);
  };

  const confirmDeleteFile = async () => {
    if (!id || !deleteFileConfirm) return;
    const fileId = deleteFileConfirm;
    setDeleteFileConfirm(null);
    try {
      await spacesApi.deleteFile(id, fileId);
      setRealFiles(prev => prev.filter(f => f.id !== fileId));
      showToast('success', '파일이 삭제됐습니다.');
    } catch {
      showToast('error', '파일 삭제에 실패했습니다.');
    }
  };

  const [ctxTab, setCtxTab] = useState('files');
  const [ctxWidth, setCtxWidth] = useState(280);
  const isDraggingCtx = useRef(false);
  const dragStartX = useRef(0);
  const dragStartW = useRef(0);

  const handleCtxDragStart = (e: React.MouseEvent) => {
    isDraggingCtx.current = true;
    dragStartX.current = e.clientX;
    dragStartW.current = ctxWidth;
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDraggingCtx.current) return;
      const diff = dragStartX.current - e.clientX;
      setCtxWidth(Math.max(200, Math.min(600, dragStartW.current + diff)));
    };
    const onUp = () => { isDraggingCtx.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ── 스페이스 상태 토글 / 삭제 ──
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const spaceName = space?.spaceName || '';
  const isInactive = space?.status === 'archived';

  const handleStatusToggle = async () => {
    if (!id || !space) return;
    const newStatus = isInactive ? 'active' : 'archived';
    try {
      await spacesApi.update(id, { status: newStatus });
      setSpace(prev => prev ? { ...prev, status: newStatus } : prev);
    } catch {
      showToast('error', '상태 변경에 실패했습니다.');
    } finally {
      setShowMoreMenu(false);
    }
  };

  const handleDeleteSpace = async () => {
    if (!id) return;
    setShowMoreMenu(false);
    if (!window.confirm('스페이스와 모든 대화 내역을 삭제할까요?\n이 작업은 되돌릴 수 없습니다.')) return;
    try {
      await spacesApi.delete(id);
      showToast('success', '스페이스가 삭제됐습니다.');
      navigate('/spaces');
    } catch {
      showToast('error', '삭제에 실패했습니다.');
    }
  };

  // ── SpaceMessage → Message 변환 ──
  const llmNode = workflow?.nodes?.find(n => n.type === 'llm');
  const modelId = (llmNode?.data?.models as string[] | undefined)?.[0] || (llmNode?.data?.model as string) || '';

  const convertedMessages = useMemo((): Message[] => {
    return messages.flatMap((m): Message[] => {
      const result: Message[] = [];
      if (m.userContent) {
        result.push({
          id: `u-${m.chatId}`,
          chatId: m.chatId,
          role: 'user',
          content: m.userContent,
          senderName: m.senderName || m.senderEmail || '',
          senderEmail: m.senderEmail || '',
        });
      }
      if (m.assistantContent) {
        const inToken = m.inputToken ?? 0;
        const outToken = m.outputToken ?? 0;
        const cost = (inToken || outToken) ? calcCostFront(modelId, inToken, outToken) : {};
        result.push({
          id: `a-${m.chatId}`,
          chatId: m.chatId,
          role: 'assistant',
          content: m.assistantContent,
          sources: m.sources as unknown as ChatSource[],
          traceContent: m.traceContent,
          elapsedMs: m.resultTime ? parseFloat(m.resultTime) * 1000 : undefined,
          usage: (inToken || outToken) ? { inputToken: inToken, outputToken: outToken, totalToken: m.totalToken ?? 0, ...cost } : undefined,
        });
      }
      return result;
    });
  }, [messages, modelId]);

  // ── 사이드바 하단: 멤버 섹션 ──
  const spaceMembers = space?.members || [];
  const sidebarBottom = (
    <div className="sd-mem-section">
      <div className="sd-mem-title">멤버</div>
      <div className="sd-mem-avatars">
        {spaceMembers.slice(0, 4).map((m, i) => (
          <div key={m.userId} className="sd-mem-av"
            style={{ background: `hsl(${m.userId.charCodeAt(0) * 37 % 360},60%,55%)`, zIndex: 5 - i }}>
            {m.name?.[0] || '?'}
          </div>
        ))}
        {spaceMembers.length > 4 && (
          <div className="sd-mem-av" style={{ background: '#888', zIndex: 0, fontSize: 8 }}>
            +{spaceMembers.length - 4}
          </div>
        )}
      </div>
      <button className="sd-invite-btn" onClick={() => setShowInvite(true)}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        멤버 초대
      </button>
    </div>
  );

  // ── 우측 컨텍스트 패널 ──
  const rightPanel = (
    <>
      <div className="sd-ctx-resize-handle" onMouseDown={handleCtxDragStart} title="드래그로 너비 조절" />
      <div className="sd-ctx-tabs">
        {['files', 'activity', 'members'].map(t => (
          <button
            key={t}
            className={`sd-ctx-tab${ctxTab === t ? ' on' : ''}`}
            onClick={() => setCtxTab(t)}
          >
            {t === 'files' ? `파일 ${realFiles.length + uploadedFiles.filter(f => f.status !== 'done').length}`
              : t === 'activity' ? '활동'
              : `멤버 ${spaceMembers.length}`}
          </button>
        ))}
      </div>
      <div className="sd-ctx-body">
        {ctxTab === 'files' && (
          <>
            <div style={{ marginBottom: 8 }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, width: '100%', padding: '8px 10px', background: 'var(--primary)', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                파일 업로드
              </button>
            </div>
            <div className="sd-ctx-info">업로드된 파일은 KB에 자동 인덱싱되어 채팅에 참조됩니다.</div>
            <div className="sd-ctx-section" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>공유 파일</span>
              <button
                onClick={loadRealFiles}
                title="파일 목록 새로고침"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: '2px 4px', borderRadius: 4, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = 'var(--primary)'; }}
                onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-3)'; }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
                </svg>
              </button>
            </div>
            {uploadedFiles.map(f => (
              <div key={f.id} className="sd-file-item">
                <div className="sd-file-icon" style={{ background: f.bg }}>{f.icon}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="sd-file-name">{f.name}</div>
                  <div className="sd-file-size">
                    {f.status === 'done' ? <span style={{ color: 'var(--success)' }}>✓ 완료</span>
                      : f.status === 'error' ? <span style={{ color: 'var(--error)' }}>✗ 오류</span>
                      : <span style={{ color: 'var(--primary)', fontSize: 10 }}>{f.step}</span>}
                  </div>
                  {f.status !== 'done' && f.status !== 'error' && (
                    <div style={{ height: 3, background: '#eee', borderRadius: 2, marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg, var(--primary), var(--secondary))', width: f.status === 'uploading' ? '55%' : '85%', transition: 'width 0.8s ease' }} />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setUploadedFiles(prev => prev.filter(u => u.id !== f.id))}
                  style={{ flexShrink: 0, padding: 3, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', borderRadius: 4, lineHeight: 1 }}
                  onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = 'var(--error)'; }}
                  onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-3)'; }}
                  title="파일 제거"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                </button>
              </div>
            ))}
            {realFiles.length === 0 && uploadedFiles.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 12, padding: '20px 0' }}>업로드된 파일이 없습니다.</div>
            )}
            {realFiles.map(f => {
              const { icon, bg } = getFileStyle(f.name);
              return (
                <div key={f.id} className="sd-file-item" style={{ gap: 6 }}>
                  <div className="sd-file-icon" style={{ background: bg }}>{icon}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="sd-file-name">{f.name}</div>
                    <div className="sd-file-size">
                      {f.status === 'indexed' ? <span style={{ color: 'var(--success)' }}>✓ 인덱싱 완료</span>
                        : f.status === 'error' ? <span style={{ color: 'var(--error)' }}>✗ 오류</span>
                        : <span style={{ color: 'var(--primary)' }}>⟳ 처리 중</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteFile(f.id)}
                    style={{ flexShrink: 0, padding: 3, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', borderRadius: 4, lineHeight: 1 }}
                    onMouseOver={e => { (e.currentTarget as HTMLElement).style.color = 'var(--error)'; }}
                    onMouseOut={e => { (e.currentTarget as HTMLElement).style.color = 'var(--text-3)'; }}
                    title="파일 삭제"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </>
        )}
        {ctxTab === 'activity' && (
          <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '20px 0', textAlign: 'center' }}>
            활동 내역이 없습니다.
          </div>
        )}
        {ctxTab === 'members' && (
          <>
            <div className="sd-ctx-section">멤버 {spaceMembers.length}명</div>
            {spaceMembers.map((m: any) => (
              <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 3px', marginBottom: 2 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: `hsl(${(m.userId || '').charCodeAt(0) * 37 % 360},60%,55%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                  {m.name?.[0] || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name}
                    {m.role === 'owner' && <span style={{ marginLeft: 4, fontSize: 10, padding: '1px 5px', background: 'var(--primary-light)', color: 'var(--primary)', borderRadius: 4 }}>소유자</span>}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{m.email}</div>
                </div>
              </div>
            ))}
            {spaceMembers.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '10px 0' }}>멤버 정보를 불러오는 중...</div>
            )}
            <div style={{ marginTop: 8 }}>
              <button className="sd-invite-btn" onClick={() => setShowInvite(true)}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                멤버 초대
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );

  // ── 로딩 중 ──
  if (loadingSpace) {
    return (
      <div className="sd-outer">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: 14 }}>
          스페이스를 불러오는 중...
        </div>
      </div>
    );
  }

  // ── 렌더 ──
  return (
    <>
      <div className="sd-outer">
        {/* ── 헤더 ── */}
        <div className="sd-header">
          <div className="sd-header-info">
            <div className="sd-header-name">{spaceName || '스페이스'}</div>
            <div className="sd-header-desc">{space?.description || ''}</div>
          </div>
          <div className="sd-header-actions">
            <span className="sd-badge-m">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
              </svg>
              멤버 {spaceMembers.length}명
            </span>
            <Button size="sm" appearance="outline" color="gray" onClick={() => navigate('/spaces')}>
              ← 목록
            </Button>
            <Button size="sm" appearance="outline" color="primary" onClick={() => setThemeModalOpen(true)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              테마
            </Button>
            <div style={{ position: 'relative' }}>
              <Button size="sm" appearance="outline" color="gray" onClick={() => setShowMoreMenu(v => !v)}>⋯</Button>
              {showMoreMenu && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowMoreMenu(false)} />
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,.12)', zIndex: 50, minWidth: 180, overflow: 'hidden' }}>
                    {isInactive ? (
                      <button onClick={handleStatusToggle} style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#0f7a40', textAlign: 'left' }}
                        onMouseOver={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-0)'; }}
                        onMouseOut={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                        스페이스 활성화
                      </button>
                    ) : (
                      <button onClick={handleStatusToggle} style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-1)', textAlign: 'left' }}
                        onMouseOver={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-0)'; }}
                        onMouseOut={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                        스페이스 비활성화
                      </button>
                    )}
                    <div style={{ height: 1, background: '#f3f4f6' }} />
                    <button onClick={handleDeleteSpace} style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--error)', textAlign: 'left' }}
                      onMouseOver={e => { (e.currentTarget as HTMLElement).style.background = 'var(--error-light)'; }}
                      onMouseOut={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                      스페이스 삭제
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── 비활성 배너 ── */}
        {isInactive && (
          <div style={{ background: '#fff8ed', border: '1px solid rgba(255,191,0,.4)', borderLeft: 'none', borderRight: 'none', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#92400e', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
            <span><strong>비활성 스페이스</strong> — 새 대화를 시작할 수 없습니다. 기존 대화 이력 및 파일은 유지됩니다.</span>
            <button onClick={handleStatusToggle} style={{ marginLeft: 'auto', padding: '3px 10px', background: '#fff', border: '1px solid rgba(255,191,0,.5)', borderRadius: 6, fontSize: 12, color: '#92400e', cursor: 'pointer', fontWeight: 600 }}>
              활성화
            </button>
          </div>
        )}

        {/* ── 채팅 영역 (ChatRenderer) ── */}
        <div className="sd-content">
          <ChatRenderer
            spaceMode
            messages={convertedMessages}
            typing={typing}
            streamingContent={streamingContent}
            streamingTrace={streamingTrace}
            streamingSources={streamingSources}
            streamingStatus={streamingStatus}
            onSend={handleSend}
            onModelChange={setOverrideModel}
            messagesEndRef={messagesEndRef}
            sessions={sessions}
            currentSessionId={currentSessionId}
            activeTheme={personalTheme || {}}
            onNewChat={() => { setMessages([]); setCurrentSessionId(null); lastMsgKeyRef.current = ''; }}
            onSelectSession={async (sessionId: string) => {
              if (!id) return;
              const msgs = await spacesApi.getMessages(id, sessionId);
              setMessages(msgs);
              setCurrentSessionId(sessionId);
            }}
            onDeleteSession={async (sessionId: string) => {
              if (!id) return;
              await spacesApi.deleteSession(id, sessionId);
              setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
              if (currentSessionId === sessionId) { setMessages([]); setCurrentSessionId(null); }
            }}
            onRenameSession={async (sessionId: string, title: string) => {
              if (!id) return;
              await spacesApi.renameSession(id, sessionId, title);
              setSessions(prev => prev.map(s => s.sessionId === sessionId ? { ...s, title } : s));
            }}
            botName={spaceName || '스페이스'}
            workflow={workflow}
            inputDisabled={isInactive}
            composerPrefix={
              <button
                title="파일 첨부"
                onClick={() => fileInputRef.current?.click()}
                className="sd-attach-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
            }
            currentUserEmail={currentUser?.email}
            sidebarBottom={sidebarBottom}
            rightPanel={rightPanel}
            rightPanelWidth={ctxWidth}
          />
        </div>
      </div>

      {/* 숨겨진 파일 업로드 인풋 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.csv,.html"
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
        onChange={handleFileChange}
      />

      {/* 테마 설정 모달 */}
      {id && (
        <ThemeEditorModal
          isOpen={themeModalOpen}
          onClose={() => setThemeModalOpen(false)}
          initialTheme={personalTheme || {}}
          botName={spaceName || '스페이스'}
          saving={savingTheme}
          showQuickPrompts={false}
          onSave={async (theme) => {
            setSavingTheme(true);
            try {
              await chatSessionApi.saveTheme(id, theme);
              setPersonalTheme(theme);
              setThemeModalOpen(false);
            } catch { /* 무시 */ }
            finally { setSavingTheme(false); }
          }}
        />
      )}

      {/* ── 파일 삭제 확인 모달 ── */}
      <Modal
        isOpen={!!deleteFileConfirm}
        onClose={() => setDeleteFileConfirm(null)}
        title="파일 삭제"
        size="sm"
        style={{ maxWidth: 380 }}
        bodyClassName="chat-confirm-body"
        footerRight={
          <>
            <Button appearance="outline" color="gray" onClick={() => setDeleteFileConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmDeleteFile}>삭제</Button>
          </>
        }
      >
        <p style={{ margin: '4px 0 12px', lineHeight: 1.6, textAlign: 'center' }}>
          파일을 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>

      {/* ── 멤버 초대 모달 ── */}
      {showInvite && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setShowInvite(false); setInviteSearch(''); }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-1)', borderRadius: 12, padding: '24px', width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,.15)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>멤버 초대</div>
            {(() => {
              const currentEmail = authApi.getUser()?.email;
              const memberEmails = spaceMembers.map(m => m.email);
              const available = systemUsers.filter(u => u.email !== currentEmail && !memberEmails.includes(u.email) && !selInviteEmails.includes(u.email));
              const filtered = inviteSearch.trim()
                ? available.filter(u => u.name.toLowerCase().includes(inviteSearch.toLowerCase()) || u.email.toLowerCase().includes(inviteSearch.toLowerCase()))
                : available;
              return (
                <div className="sm-wrap">
                  <div className="input sm-trigger">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2">
                      <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      className="input-control sm-input"
                      placeholder="이름 또는 이메일 검색..."
                      value={inviteSearch}
                      onChange={e => setInviteSearch(e.target.value)}
                      onFocus={() => setInviteDropOpen(true)}
                      onBlur={() => setTimeout(() => setInviteDropOpen(false), 150)}
                    />
                  </div>
                  {inviteDropOpen && filtered.length > 0 && (
                    <div className="sm-dropdown">
                      {filtered.map(u => (
                        <div key={u.email} className="sm-item"
                          onMouseDown={() => { setSelInviteEmails(prev => [...prev, u.email]); setInviteSearch(''); setInviteDropOpen(false); }}>
                          <div className="sm-av">{u.name[0]}</div>
                          <div>
                            <div className="sm-name">{u.name}</div>
                            <div className="sm-email">{u.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {selInviteEmails.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selInviteEmails.map(email => {
                  const u = systemUsers.find(s => s.email === email);
                  return (
                    <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px 3px 5px', background: 'var(--primary-light)', borderRadius: 999, fontSize: 12, color: 'var(--primary)' }}>
                      <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{(u?.name || email)[0]}</span>
                      {u?.name || email}
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: 0, lineHeight: 1, fontSize: 13 }}
                        onClick={() => setSelInviteEmails(prev => prev.filter(e => e !== email))}>✕</button>
                    </span>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <Button appearance="outline" color="gray" onClick={() => { setShowInvite(false); setInviteSearch(''); setSelInviteEmails([]); }}>취소</Button>
              <Button color="primary" onClick={handleInvite} disabled={inviting || selInviteEmails.length === 0}>
                {inviting ? '초대 중...' : `초대 (${selInviteEmails.length})`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SpaceDetailPage;
